import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useAlarmBadge } from '../context/AlarmBadgeContext';

const Sidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();

  const isActive = (path) => location.pathname === path;

  // Nëse përdoruesi ka allowed_routes, shfaq vetëm ato linqe; përndryshe sipas rolit.
  const allowedPaths = user?.allowed_routes && Array.isArray(user.allowed_routes) && user.allowed_routes.length > 0
    ? user.allowed_routes
    : null;

  const showAlarmBadge = !!user;
  const alarmBadgeCount = useAlarmBadge();

  const menuItems = [
    { 
      path: '/dashboard', 
      labelKey: 'home', 
      roles: ['SUPERADMIN', 'ADMIN', 'VIEWER'],
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    },
    { 
      path: '/devices', 
      labelKey: 'devices', 
      roles: ['SUPERADMIN', 'ADMIN', 'VIEWER'],
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      )
    },
    { 
      path: '/checkin', 
      labelKey: 'checkin', 
      roles: ['SUPERADMIN', 'ADMIN', 'VIEWER'],
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    { 
      path: '/reports', 
      labelKey: 'reports', 
      roles: ['SUPERADMIN', 'ADMIN', 'VIEWER'],
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    { 
      path: '/alarms', 
      labelKey: 'alarms', 
      roles: ['SUPERADMIN', 'ADMIN', 'VIEWER'],
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      )
    },
    { 
      path: '/companies', 
      labelKey: 'companies', 
      roles: ['SUPERADMIN'],
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    { 
      path: '/users', 
      labelKey: 'users', 
      roles: ['SUPERADMIN', 'ADMIN'],
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
  ];

  return (
    <aside className="w-14 bg-white dark:bg-gray-800 shadow-lg h-full flex flex-col border-r border-gray-200 dark:border-gray-700">
      {/* Small spacing at top */}
      <div className="py-3"></div>

      <nav className="flex-1 flex flex-col items-center space-y-1 px-1.5">
        {menuItems.map((item) => {
          if (!item.roles.includes(user?.role)) return null;
          if (allowedPaths !== null && !allowedPaths.includes(item.path)) return null;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`w-full flex items-center justify-center p-2.5 rounded-md transition group relative ${
                isActive(item.path) 
                  ? 'bg-blue-500 text-white shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
              title={item.labelKey ? t(item.labelKey) : item.label}
            >
              <span className="relative inline-flex">
                {item.icon}
                {item.path === '/alarms' && showAlarmBadge && alarmBadgeCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[0.875rem] h-[0.875rem] px-0.5 flex items-center justify-center rounded-full bg-red-600 text-white text-[8px] font-bold leading-none border border-white dark:border-gray-800">
                    {alarmBadgeCount > 99 ? '99+' : alarmBadgeCount}
                  </span>
                )}
              </span>
              
              {/* Tooltip */}
              <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap pointer-events-none z-50">
                {item.labelKey ? t(item.labelKey) : item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* System Settings – gjithmonë i dukshëm (nuk kontrollohet nga allowed_routes) */}
      <div className="flex-shrink-0 py-2 border-t border-gray-200 dark:border-gray-700 px-1.5">
        <Link
          to="/me/settings"
          className={`w-full flex items-center justify-center p-2.5 rounded-md transition group relative ${
            isActive('/me/settings')
              ? 'bg-blue-500 text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
          title={t('systemSettings')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap pointer-events-none z-50">
            {t('systemSettings')}
          </span>
        </Link>
      </div>
    </aside>
  );
};

export default Sidebar;
